import { useState, useCallback, useRef, useEffect, Fragment } from 'react';
import { toast } from 'sonner';
import {
    Upload, FileSpreadsheet, Play, Square, Brain, Database,
    CheckCircle2, XCircle, Clock, Loader2, Zap, BarChart3,
    AlertTriangle, RefreshCw, ChevronDown, ChevronUp, Eye, Trash2,
    Plus, Package, Tag, Download, Upload as UploadIcon, Star, Layers,
    Pencil, RotateCcw, Save, X
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { apiClient } from '../lib/api';

// ─── Column mapping for Excel ───────────────────────────────────────
const COLUMN_MAP = {
    partNumber: ['Partnum', 'Part Number', 'PART NUMBER', 'PartNumber', 'ManufacturerNumber', 'MANUFACTURER NUMBER 1', 'MANUFACTURER PART NUMBER 1'],
    description1: ['ITEM_DESCRIPTION 1', 'Description 1', 'Description', 'DESCRIPTION', 'ITEM DESCRIPTION', 'CLEANSED/ENRICHED'],
    description2: ['ITEM_DESCRIPTION 2', 'Description 2'],
    nounModifier: ['Noun/Modifier', 'NOUN/MODIFIER', 'NounModifier', 'Noun Modifier'],
    url: ['MANUFACTURER URL 1', 'ENRICHMENT URL 1', 'URL', 'Url', 'Source URL', 'MANUFACTURER URL'],
};

const MAX_PROPERTIES = 25;

function findColumn(headers, candidates) {
    for (const c of candidates) {
        const found = headers.find(h => h.toLowerCase().trim() === c.toLowerCase().trim());
        if (found) return found;
    }
    return null;
}

function extractPropertiesFromRow(row, headers) {
    const propertyNames = [];
    const mappedOutput = {};

    for (let i = 1; i <= MAX_PROPERTIES; i++) {
        const propKey = headers.find(h => h.toUpperCase() === `PROPERTY${i}`);
        const valKey = headers.find(h => h.toUpperCase() === `VALUE_OF_PROPERTY${i}`);

        if (propKey && row[propKey]) {
            const name = String(row[propKey]).trim();
            const value = valKey ? String(row[valKey] || '').trim() : '';
            if (name) {
                propertyNames.push(name);
                mappedOutput[name] = value;
            }
        }
    }

    return { propertyNames, mappedOutput };
}

// ─── Status badge ───────────────────────────────────────────────────
function StatusBadge({ status }) {
    const map = {
        pending: { icon: Clock, color: 'text-slate-400 bg-slate-50 border-slate-200', label: 'Pending' },
        scraping: { icon: Loader2, color: 'text-blue-600 bg-blue-50 border-blue-200', label: 'Scraping', animate: true },
        cleaning: { icon: Loader2, color: 'text-purple-600 bg-purple-50 border-purple-200', label: 'Cleaning', animate: true },
        saving: { icon: Loader2, color: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Saving', animate: true },
        completed: { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'Done' },
        failed: { icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200', label: 'Failed' },
        skipped: { icon: AlertTriangle, color: 'text-amber-600 bg-amber-50 border-amber-200', label: 'No URL' },
    };
    const s = map[status] || map.pending;
    const Icon = s.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg border ${s.color}`}>
            <Icon className={`w-3.5 h-3.5 ${s.animate ? 'animate-spin' : ''}`} />
            {s.label}
        </span>
    );
}

// ─── Main Component ─────────────────────────────────────────────────
export default function TrainingDashboardPage() {
    const [rows, setRows] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [processedCount, setProcessedCount] = useState(0);
    const [logs, setLogs] = useState([]);
    const [trainingStatus, setTrainingStatus] = useState(null);
    const [isTraining, setIsTraining] = useState(false);
    const [customEpochs, setCustomEpochs] = useState(10);
    const [customBatchSize, setCustomBatchSize] = useState(4);
    const [customLearningRate, setCustomLearningRate] = useState('3e-4');
    const [showLogs, setShowLogs] = useState(true);
    const [expandedRow, setExpandedRow] = useState(null);
    const [fileName, setFileName] = useState('');
    const [resetModal, setResetModal] = useState(null); // null | 'records' | 'content' | 'model'
    const [resetConfirmText, setResetConfirmText] = useState('');
    const stopRef = useRef(false);
    const logEndRef = useRef(null);
    const fileInputRef = useRef(null);

    // ── Model Version Management state ──
    const [versions, setVersions] = useState([]);
    const [selectedVersionId, setSelectedVersionId] = useState(null);
    const [showCreateVersion, setShowCreateVersion] = useState(false);
    const [newVersionName, setNewVersionName] = useState('');
    const [newVersionDesc, setNewVersionDesc] = useState('');
    const [creatingVersion, setCreatingVersion] = useState(false);
    const [deleteVersionModal, setDeleteVersionModal] = useState(null); // version object or null
    const [deleteVersionConfirm, setDeleteVersionConfirm] = useState('');
    const [versionStats, setVersionStats] = useState(null);
    const [exportingColab, setExportingColab] = useState(false);
    const [versionRecords, setVersionRecords] = useState([]);
    const [recordsPagination, setRecordsPagination] = useState({ page: 1, limit: 100, total: 0, totalPages: 0 });
    const [loadingRecords, setLoadingRecords] = useState(false);
    const [showRecords, setShowRecords] = useState(false);
    const [expandedRecord, setExpandedRecord] = useState(null);
    const [editingRecord, setEditingRecord] = useState(null); // full record object being edited
    const [editForm, setEditForm] = useState({});
    const [savingEdit, setSavingEdit] = useState(false);
    const [reprocessingId, setReprocessingId] = useState(null);
    const [deletingRecordId, setDeletingRecordId] = useState(null);

    // ── Fetch training status on mount ──
    useEffect(() => {
        fetchTrainingStatus();
        fetchVersions();
    }, []);

    // ── Fetch version stats when selected version changes ──
    useEffect(() => {
        if (selectedVersionId) {
            fetchVersionStats(selectedVersionId);
            setShowRecords(false);
            setVersionRecords([]);
        } else {
            setVersionStats(null);
            setVersionRecords([]);
        }
    }, [selectedVersionId]);

    const fetchTrainingStatus = async () => {
        try {
            const data = await apiClient.trainingStatus();
            if (data.success) setTrainingStatus(data);
        } catch (e) {
            console.warn('Could not fetch training status:', e.message);
        }
    };

    // ── Version Management Functions ──
    const fetchVersions = async () => {
        try {
            const data = await apiClient.listModelVersions();
            if (data.success) {
                setVersions(data.versions || []);
                // Auto-select active version, or first version
                if (!selectedVersionId && data.versions?.length > 0) {
                    const active = data.versions.find(v => v.is_active);
                    setSelectedVersionId(active?.id || data.versions[0].id);
                }
            }
        } catch (e) {
            console.warn('Could not fetch versions:', e.message);
        }
    };

    const fetchVersionStats = async (versionId) => {
        try {
            const data = await apiClient.getModelVersionStats(versionId);
            if (data.success) setVersionStats(data.stats);
        } catch (e) {
            console.warn('Could not fetch version stats:', e.message);
        }
    };

    const fetchVersionRecords = async (versionId, page = 1) => {
        setLoadingRecords(true);
        try {
            const data = await apiClient.getModelVersionRecords(versionId, page, 100);
            if (data.success) {
                setVersionRecords(data.records || []);
                setRecordsPagination(data.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
            }
        } catch (e) {
            console.warn('Could not fetch version records:', e.message);
        }
        setLoadingRecords(false);
    };

    // ── Record actions ──
    const handleDeleteRecord = async (recordId) => {
        if (!confirm('Delete this training record?')) return;
        setDeletingRecordId(recordId);
        try {
            const data = await apiClient.deleteTrainingRecord(recordId);
            if (data.success) {
                toast.success('Record deleted');
                setVersionRecords(prev => prev.filter(r => r.id !== recordId));
                setRecordsPagination(prev => ({ ...prev, total: prev.total - 1 }));
                if (selectedVersionId) fetchVersionStats(selectedVersionId);
                fetchVersions();
            }
        } catch (e) {
            toast.error('Delete failed: ' + (e.response?.data?.error || e.message));
        }
        setDeletingRecordId(null);
    };

    const handleEditRecord = (rec) => {
        setEditingRecord(rec);
        setEditForm({
            description: rec.description || '',
            nounModifier: rec.noun_modifier || '',
            mappedOutput: rec.mapped_output || {},
        });
    };

    const handleSaveEdit = async () => {
        if (!editingRecord) return;
        setSavingEdit(true);
        try {
            const data = await apiClient.updateTrainingRecord(editingRecord.id, {
                description: editForm.description,
                nounModifier: editForm.nounModifier,
                mappedOutput: editForm.mappedOutput,
            });
            if (data.success) {
                toast.success('Record updated');
                // Update in local state
                setVersionRecords(prev => prev.map(r => r.id === editingRecord.id ? {
                    ...r,
                    description: editForm.description,
                    noun_modifier: editForm.nounModifier,
                    mapped_output: editForm.mappedOutput,
                    value_count: Object.values(editForm.mappedOutput).filter(v => v && String(v).trim()).length,
                } : r));
                setEditingRecord(null);
            }
        } catch (e) {
            toast.error('Update failed: ' + (e.response?.data?.error || e.message));
        }
        setSavingEdit(false);
    };

    const handleReprocessRecord = async (recordId) => {
        setReprocessingId(recordId);
        try {
            const data = await apiClient.reprocessTrainingRecord(recordId);
            if (data.success) {
                toast.success('Record reprocessed');
                // Refresh the records list
                if (selectedVersionId) fetchVersionRecords(selectedVersionId, recordsPagination.page);
            }
        } catch (e) {
            toast.error('Reprocess failed: ' + (e.response?.data?.error || e.message));
        }
        setReprocessingId(null);
    };

    const handleCreateVersion = async () => {
        if (!newVersionName.trim()) {
            toast.error('Version name is required');
            return;
        }
        setCreatingVersion(true);
        try {
            const data = await apiClient.createModelVersion({
                name: newVersionName.trim(),
                description: newVersionDesc.trim(),
            });
            if (data.success) {
                toast.success(`Version "${newVersionName}" created`);
                setNewVersionName('');
                setNewVersionDesc('');
                setShowCreateVersion(false);
                await fetchVersions();
                setSelectedVersionId(data.id);
            }
        } catch (err) {
            toast.error(err.message || 'Failed to create version');
        }
        setCreatingVersion(false);
    };

    const handleDeleteVersion = async (version) => {
        try {
            const data = await apiClient.deleteModelVersion(version.id);
            toast.success(data.message || 'Version deleted');
            setDeleteVersionModal(null);
            setDeleteVersionConfirm('');
            await fetchVersions();
            if (selectedVersionId === version.id) {
                setSelectedVersionId(versions.find(v => v.id !== version.id)?.id || null);
            }
        } catch (err) {
            toast.error(err.message || 'Failed to delete version');
        }
    };

    const handleActivateVersion = async (versionId) => {
        try {
            await apiClient.activateModelVersion(versionId);
            toast.success('Version activated');
            fetchVersions();
        } catch (err) {
            toast.error(err.message || 'Failed to activate version');
        }
    };

    const handleExportColab = async () => {
        if (!selectedVersionId) {
            toast.error('Select a model version first');
            return;
        }
        setExportingColab(true);
        try {
            await apiClient.exportColabData(selectedVersionId);
            toast.success('Training data exported for Google Colab');
            addLog(`📦 Exported training data for Colab (version #${selectedVersionId})`, 'success');
        } catch (err) {
            toast.error(err.message || 'Export failed');
            addLog(`Export failed: ${err.message}`, 'error');
        }
        setExportingColab(false);
    };

    const addLog = useCallback((msg, type = 'info') => {
        const ts = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, { ts, msg, type }]);
        setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }, []);

    // ── Excel Upload Handler ──
    const handleFileUpload = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);
        addLog(`Loading: ${file.name}`, 'info');

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const wb = XLSX.read(evt.target.result, { type: 'array' });
                const wsName = wb.SheetNames[0];
                const ws = wb.Sheets[wsName];
                const jsonData = XLSX.utils.sheet_to_json(ws, { defval: '' });

                if (jsonData.length === 0) {
                    toast.error('Excel file is empty');
                    return;
                }

                const hdrs = Object.keys(jsonData[0]);
                setHeaders(hdrs);

                // Auto-map columns
                const mapping = {};
                for (const [key, candidates] of Object.entries(COLUMN_MAP)) {
                    mapping[key] = findColumn(hdrs, candidates);
                }
                setColumnMapping(mapping);

                // Add row metadata
                const rowsWithMeta = jsonData.map((r, i) => {
                    const { propertyNames, mappedOutput } = extractPropertiesFromRow(r, hdrs);
                    return {
                        ...r,
                        _index: i,
                        _status: 'pending',
                        _propertyNames: propertyNames,
                        _mappedOutput: mappedOutput,
                        _cleanedContent: '',
                        _scrapeStatus: '',
                        _error: null,
                    };
                });

                setRows(rowsWithMeta);
                setProcessedCount(0);
                addLog(`Loaded ${rowsWithMeta.length} rows from "${wsName}" (${hdrs.length} columns)`, 'success');
                addLog(`Auto-mapped: PN=${mapping.partNumber || '?'}, Desc 1=${mapping.description1 || '?'}, Desc 2=${mapping.description2 || '?'}, N/M=${mapping.nounModifier || '?'}, URL=${mapping.url || '?'}`, 'info');

                const withProps = rowsWithMeta.filter(r => r._propertyNames.length >= 2).length;
                const withUrl = rowsWithMeta.filter(r => mapping.url && r[mapping.url]).length;
                addLog(`${withProps} rows have properties, ${withUrl} rows have URLs`, 'info');

                toast.success(`Loaded ${rowsWithMeta.length} rows`);
            } catch (err) {
                toast.error('Failed to parse Excel: ' + err.message);
                addLog(`Parse error: ${err.message}`, 'error');
            }
        };
        reader.readAsArrayBuffer(file);
    }, [addLog]);

    // ── Process All Rows ──
    const processAllRows = useCallback(async () => {
        if (rows.length === 0) return;
        setIsProcessing(true);
        stopRef.current = false;
        setProcessedCount(0);

        addLog(`Starting processing ${rows.length} rows...`, 'info');

        for (let i = 0; i < rows.length; i++) {
            if (stopRef.current) {
                addLog(`Stopped by user at row ${i + 1}`, 'warn');
                break;
            }

            const row = rows[i];

            // Skip rows with too few properties
            if (row._propertyNames.length < 2) {
                setRows(prev => prev.map((r, idx) => idx === i ? { ...r, _status: 'skipped', _error: 'Too few properties' } : r));
                addLog(`Row ${i + 1}: Skipped (${row._propertyNames.length} properties)`, 'warn');
                setProcessedCount(prev => prev + 1);
                continue;
            }

            const url = columnMapping.url ? String(row[columnMapping.url] || '').trim() : '';
            const description1 = columnMapping.description1 ? String(row[columnMapping.description1] || '').trim() : '';
            const description2 = columnMapping.description2 ? String(row[columnMapping.description2] || '').trim() : '';
            const description = [description1, description2].filter(Boolean).join('\n');
            const nounModifier = columnMapping.nounModifier ? String(row[columnMapping.nounModifier] || '').trim() : '';
            const partNumber = columnMapping.partNumber ? String(row[columnMapping.partNumber] || '').trim() : '';

            // Update status to scraping
            setRows(prev => prev.map((r, idx) => idx === i ? { ...r, _status: url ? 'scraping' : 'saving' } : r));
            addLog(`Row ${i + 1}: ${partNumber || description.slice(0, 40)} — ${url ? 'Scraping...' : 'Saving (no URL)'}`, 'info');

            try {
                const data = await apiClient.processExcelRow({
                    url,
                    description,
                    nounModifier,
                    partNumber,
                    propertyNames: row._propertyNames,
                    mappedOutput: row._mappedOutput,
                    versionId: selectedVersionId || null,
                });

                if (data.success) {
                    setRows(prev => prev.map((r, idx) => idx === i ? {
                        ...r,
                        _status: 'completed',
                        _scrapeStatus: data.scrapeStatus,
                        _cleanedContent: data.hasCleanedContent ? `${data.cleanedContentLength} chars` : '',
                        _error: data.scrapeError,
                    } : r));

                    // Log each step in detail
                    if (data.steps) {
                        const stepIcons = { firecrawl: '🔥', cleanup: '🧹', gemini: '🤖', save: '💾' };
                        const stepTypes = { success: 'success', failed: 'error', error: 'error', fallback: 'warn', skipped: 'warn', started: 'info' };

                        for (const s of data.steps) {
                            const icon = stepIcons[s.step] || '▪';
                            const type = stepTypes[s.status] || 'info';
                            addLog(`  ${icon} [${s.step.toUpperCase()}] ${s.detail}`, type);

                            // Show content preview for key steps
                            if (s.metadata?.contentPreview && (s.step === 'firecrawl' || s.step === 'gemini')) {
                                const preview = s.metadata.contentPreview.replace(/\n/g, ' ').substring(0, 150);
                                addLog(`     ↳ Preview: "${preview}"`, 'info');
                            }
                            if (s.metadata?.model) {
                                addLog(`     ↳ Model: ${s.metadata.model}, Reduction: ${s.metadata.reduction}`, 'info');
                            }
                            if (s.metadata?.properties) {
                                addLog(`     ↳ Properties: ${s.metadata.properties.join(', ')}`, 'info');
                            }
                        }
                    }

                    const statusEmoji = data.scrapeStatus === 'cleaned' ? '✅' : data.scrapeStatus === 'raw' ? '⚠️' : data.scrapeStatus === 'skipped' ? '📝' : '❌';
                    addLog(`Row ${i + 1}: ${statusEmoji} DONE — pair #${data.pairId} (${data.cleanedContentLength} chars)`, 'success');
                } else {
                    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, _status: 'failed', _error: data.error } : r));
                    addLog(`Row ${i + 1}: ❌ Failed — ${data.error}`, 'error');
                }
            } catch (err) {
                setRows(prev => prev.map((r, idx) => idx === i ? { ...r, _status: 'failed', _error: err.message } : r));
                addLog(`Row ${i + 1}: ❌ Error — ${err.message}`, 'error');
            }

            setProcessedCount(prev => prev + 1);

            // ── 30-second cooldown between rows to avoid scraping blocks ──
            if (i < rows.length - 1 && !stopRef.current) {
                addLog(`⏳ Cooldown: waiting 30s before next row...`, 'info');
                for (let sec = 30; sec > 0; sec--) {
                    if (stopRef.current) break;
                    setRows(prev => prev.map((r, idx) => idx === i + 1 ? { ...r, _status: `wait_${sec}s` } : r));
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
                if (!stopRef.current) {
                    addLog(`✅ Cooldown complete — proceeding to next row`, 'info');
                }
            }
        }

        setIsProcessing(false);
        addLog('Processing complete!', 'success');
        fetchTrainingStatus();
        fetchVersions();
        if (selectedVersionId) fetchVersionStats(selectedVersionId);
        toast.success('All rows processed');
    }, [rows, columnMapping, addLog]);

    // ── Stop Processing ──
    const stopProcessing = useCallback(() => {
        stopRef.current = true;
        addLog('Stopping...', 'warn');
    }, [addLog]);

    // ── Live Training Progress ──
    const [trainingProgress, setTrainingProgress] = useState(null);
    const progressPollRef = useRef(null);

    const startProgressPolling = useCallback(() => {
        // Clear any existing interval
        if (progressPollRef.current) clearInterval(progressPollRef.current);

        progressPollRef.current = setInterval(async () => {
            try {
                const data = await apiClient.trainingStatus();
                if (data.success && data.trainingProgress) {
                    setTrainingProgress(data.trainingProgress);

                    // Stop polling when training is done
                    if (data.trainingProgress.status === 'completed') {
                        clearInterval(progressPollRef.current);
                        progressPollRef.current = null;
                        setIsTraining(false);
                        setTrainingStatus(data); // refresh final stats
                        addLog(`Training completed! Final loss: ${data.trainingProgress.loss?.toFixed(4) || 'N/A'}`, 'success');
                        toast.success('Model training completed!');
                    }
                }
            } catch (e) { /* ignore polling errors */ }
        }, 3000);
    }, [addLog]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (progressPollRef.current) clearInterval(progressPollRef.current);
        };
    }, []);

    // ── Start Training ──
    const handleStartTraining = useCallback(async () => {
        setIsTraining(true);
        setTrainingProgress(null);
        addLog('Starting model training pipeline...', 'info');
        try {
            const data = await apiClient.startTraining({ epochs: customEpochs, batchSize: customBatchSize, learningRate: customLearningRate });
            if (data.success) {
                addLog(`Training started: ${data.config.totalRecords} records, ${data.config.epochs} epochs`, 'success');
                toast.success('Training pipeline started (runs in background)');
                // Start polling for progress
                startProgressPolling();
            } else {
                addLog(`Training failed to start: ${data.error}`, 'error');
                toast.error(data.error);
                setIsTraining(false);
            }
        } catch (err) {
            addLog(`Training error: ${err.message}`, 'error');
            toast.error(err.message);
            setIsTraining(false);
        }
    }, [addLog, startProgressPolling, customEpochs, customBatchSize, customLearningRate]);

    // ── Reset Handlers ──
    const handleReset = useCallback(async (type) => {
        try {
            let data;
            if (type === 'records') {
                data = await apiClient.resetTrainingRecords();
                addLog('🗑 All training records deleted', 'warn');
            } else if (type === 'content') {
                data = await apiClient.resetCleanedContent();
                addLog(`🗑 Cleaned content cleared from ${data.affected} records`, 'warn');
            } else if (type === 'model') {
                data = await apiClient.resetTrainedModel();
                addLog(`🧠 AI trained memory reset — ${data.deleted} files deleted`, 'warn');
            }
            toast.success(data.message);
            fetchTrainingStatus();
        } catch (err) {
            toast.error(err.message);
            addLog(`Reset failed: ${err.message}`, 'error');
        }
        setResetModal(null);
        setResetConfirmText('');
    }, [addLog]);

    // ── Computed stats ──
    const completed = rows.filter(r => r._status === 'completed').length;
    const failed = rows.filter(r => r._status === 'failed').length;
    const withUrl = rows.filter(r => columnMapping.url && r[columnMapping.url]).length;
    const withProps = rows.filter(r => r._propertyNames.length >= 2).length;

    return (
        <div className="max-w-[1600px] mx-auto space-y-6">
            {/* ── Header ── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
                            <Brain className="w-5 h-5 text-white" />
                        </div>
                        AI Training Dashboard
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">Upload Excel → Scrape & Clean URLs → Train Model</p>
                </div>

                <div className="flex items-center gap-3">
                    <button onClick={fetchTrainingStatus} className="btn-secondary text-sm py-2">
                        <RefreshCw className="w-4 h-4" /> Refresh Status
                    </button>
                </div>
            </div>

            {/* ── Model Version Selector ── */}
            <div className="card" id="version-selector">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <Layers className="w-5 h-5 text-violet-600" />
                        Model Version
                    </h2>
                    <button
                        onClick={() => setShowCreateVersion(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-xl transition-colors"
                    >
                        <Plus className="w-4 h-4" /> New Version
                    </button>
                </div>

                {/* Version Pills */}
                {versions.length === 0 ? (
                    <div className="text-center py-6 text-slate-400">
                        <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No model versions yet. Create one to start.</p>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {versions.map(v => {
                            const isSelected = v.id === selectedVersionId;
                            const isActive = v.is_active;
                            return (
                                <button
                                    key={v.id}
                                    onClick={() => setSelectedVersionId(v.id)}
                                    className={`group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
                                        isSelected
                                            ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white border-transparent shadow-md shadow-violet-200'
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-violet-300 hover:bg-violet-50'
                                    }`}
                                >
                                    {isActive ? <Star className="w-3.5 h-3.5 fill-current" /> : null}
                                    <Tag className="w-3.5 h-3.5" />
                                    {v.name}
                                    <span className={`text-xs px-1.5 py-0.5 rounded-md ${
                                        isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                                    }`}>
                                        {v.record_count || 0}
                                    </span>
                                    {v.has_model && (
                                        <span className={`text-xs px-1.5 py-0.5 rounded-md ${
                                            isSelected ? 'bg-emerald-400/30 text-white' : 'bg-emerald-50 text-emerald-600'
                                        }`}>
                                            Trained
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Selected Version Details */}
                {selectedVersionId && (() => {
                    const sv = versions.find(v => v.id === selectedVersionId);
                    if (!sv) return null;
                    return (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-3 border-t border-slate-100">
                            <div className="flex-1 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                                <span><strong>Status:</strong> <span className={`font-semibold ${
                                    sv.status === 'trained' ? 'text-emerald-600' :
                                    sv.status === 'training' ? 'text-violet-600' :
                                    sv.status === 'failed' ? 'text-red-600' : 'text-slate-500'
                                }`}>{sv.status || 'draft'}</span></span>
                                {versionStats && (
                                    <>
                                        <span className="text-slate-300">|</span>
                                        <span><strong>{versionStats.total}</strong> records</span>
                                        <span className="text-slate-300">|</span>
                                        <span><strong>{versionStats.withContent}</strong> with content</span>
                                    </>
                                )}
                                {sv.description && (
                                    <>
                                        <span className="text-slate-300">|</span>
                                        <span className="text-slate-400 italic">{sv.description}</span>
                                    </>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {!sv.is_active && (
                                    <button
                                        onClick={() => handleActivateVersion(sv.id)}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors"
                                        title="Set as active model for inference"
                                    >
                                        <Star className="w-3 h-3" /> Activate
                                    </button>
                                )}
                                <button
                                    onClick={() => setDeleteVersionModal(sv)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                                >
                                    <Trash2 className="w-3 h-3" /> Delete
                                </button>
                            </div>
                        </div>
                    );
                })()}

                {/* ── Colab Export Section ── */}
                {selectedVersionId && versionStats && versionStats.total > 0 && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                                    <Package className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-amber-900">Export for Google Colab</p>
                                    <p className="text-xs text-amber-600">
                                        Download {versionStats.total} training record{versionStats.total !== 1 ? 's' : ''} as JSONL for Colab training
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleExportColab}
                                disabled={exportingColab}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-xl shadow-sm transition-all disabled:opacity-50"
                            >
                                {exportingColab ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                {exportingColab ? 'Exporting...' : 'Download JSONL'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Create Version Modal ── */}
            {showCreateVersion && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl border border-slate-200">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center">
                                <Plus className="w-5 h-5 text-violet-600" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900">Create Model Version</h3>
                        </div>

                        <div className="space-y-3 mb-5">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Version Name *</label>
                                <input
                                    type="text"
                                    value={newVersionName}
                                    onChange={(e) => setNewVersionName(e.target.value)}
                                    placeholder='e.g., "Joy H1" or "Production v2"'
                                    className="input-field"
                                    autoFocus
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreateVersion()}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                                <input
                                    type="text"
                                    value={newVersionDesc}
                                    onChange={(e) => setNewVersionDesc(e.target.value)}
                                    placeholder="Optional notes about this version"
                                    className="input-field"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowCreateVersion(false); setNewVersionName(''); setNewVersionDesc(''); }}
                                className="flex-1 btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateVersion}
                                disabled={!newVersionName.trim() || creatingVersion}
                                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                                    !newVersionName.trim() || creatingVersion
                                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-sm'
                                }`}
                            >
                                {creatingVersion ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                Create
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delete Version Confirmation Modal ── */}
            {deleteVersionModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl border border-slate-200">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-10 w-10 rounded-xl bg-red-100 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-red-600" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900">
                                Delete &quot;{deleteVersionModal.name}&quot;?
                            </h3>
                        </div>

                        <p className="text-sm text-slate-600 mb-4">
                            This will permanently delete the model version, <strong>all {deleteVersionModal.record_count || 0} training records</strong>,
                            and any trained model files. This cannot be undone.
                        </p>

                        <div className="mb-4">
                            <p className="text-sm font-semibold text-red-600 mb-2">
                                Type <code className="bg-red-50 px-2 py-0.5 rounded text-red-700 font-bold">DELETE</code> to confirm:
                            </p>
                            <input
                                type="text"
                                value={deleteVersionConfirm}
                                onChange={(e) => setDeleteVersionConfirm(e.target.value)}
                                placeholder="Type DELETE"
                                className="input-field border-red-300 focus:ring-red-100 focus:border-red-400"
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => { setDeleteVersionModal(null); setDeleteVersionConfirm(''); }}
                                className="flex-1 btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDeleteVersion(deleteVersionModal)}
                                disabled={deleteVersionConfirm !== 'DELETE'}
                                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
                                    deleteVersionConfirm !== 'DELETE'
                                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                        : 'bg-red-600 hover:bg-red-700 text-white'
                                }`}
                            >
                                <Trash2 className="w-4 h-4" /> Delete Version
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Model & Data Status Cards ── */}
            {(() => {
                // Use version-specific stats when a version is selected
                const sv = selectedVersionId ? versions.find(v => v.id === selectedVersionId) : null;
                const recordCount = selectedVersionId && versionStats ? versionStats.total : (trainingStatus?.stats?.total || 0);
                const contentCount = selectedVersionId && versionStats ? versionStats.withContent : (trainingStatus?.stats?.withContent || 0);
                const hasTrainingLog = sv?.training_progress || trainingStatus?.trainingLog;
                const epochCount = sv?.training_progress?.epoch || (trainingStatus?.trainingLog ? trainingStatus.trainingLog.length : 0);

                return (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="card flex items-center gap-4">
                        <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${trainingStatus?.modelAvailable ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                            <Zap className={`w-6 h-6 ${trainingStatus?.modelAvailable ? 'text-emerald-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Model Server</p>
                            <p className={`text-lg font-bold ${trainingStatus?.modelAvailable ? 'text-emerald-600' : 'text-slate-400'}`}>
                                {trainingStatus?.modelAvailable ? 'Online' : 'Offline'}
                            </p>
                        </div>
                    </div>

                    <div className="card flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <Database className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs text-slate-500 font-medium">Training Records</p>
                            <p className="text-lg font-bold text-blue-600">{recordCount}</p>
                        </div>
                        {recordCount > 0 && (
                            <button onClick={() => setResetModal('records')} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Reset training records">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    <div className="card flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
                            <BarChart3 className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs text-slate-500 font-medium">With Cleaned Content</p>
                            <p className="text-lg font-bold text-purple-600">{contentCount}</p>
                        </div>
                        {contentCount > 0 && (
                            <button onClick={() => setResetModal('content')} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Reset cleaned content">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    <div className="card flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                            <Brain className="w-6 h-6 text-amber-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs text-slate-500 font-medium">Last Training</p>
                            <p className="text-lg font-bold text-amber-600">
                                {epochCount > 0 ? `${epochCount} epochs` : 'Never'}
                            </p>
                        </div>
                        {hasTrainingLog && (
                            <button onClick={() => setResetModal('model')} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Reset AI trained memory">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
                );
            })()}

            {/* ── Version Training Records Grid ── */}
            {selectedVersionId && versionStats && versionStats.total > 0 && (
                <div className="card" id="version-records">
                    <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => {
                            const next = !showRecords;
                            setShowRecords(next);
                            if (next && versionRecords.length === 0) fetchVersionRecords(selectedVersionId);
                        }}
                    >
                        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                            <Database className="w-5 h-5 text-indigo-600" />
                            Training Records
                            <span className="text-sm font-normal text-slate-400">({versionStats.total})</span>
                        </h2>
                        <div className="flex items-center gap-2">
                            {loadingRecords && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                            {showRecords ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                        </div>
                    </div>

                    {showRecords && (
                        <div className="mt-4">
                            <div className="overflow-x-auto rounded-xl border border-slate-200">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                            <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">#</th>
                                            <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Part Number</th>
                                            <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Noun/Modifier</th>
                                            <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider max-w-[250px]">Description</th>
                                            <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Props</th>
                                            <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Values</th>
                                            <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Content</th>
                                            <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                                            <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider w-28">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {versionRecords.map((rec, idx) => (
                                            <Fragment key={rec.id}>
                                                <tr
                                                    className={`hover:bg-slate-50 transition-colors cursor-pointer ${
                                                        expandedRecord === rec.id ? 'bg-indigo-50/50' : ''
                                                    }`}
                                                    onClick={() => setExpandedRecord(expandedRecord === rec.id ? null : rec.id)}
                                                >
                                                    <td className="px-3 py-2 text-slate-400 font-mono text-xs">{rec.id}</td>
                                                    <td className="px-3 py-2 font-semibold text-slate-800 max-w-[150px] truncate">
                                                        {rec.manufacturer_pn || <span className="text-slate-300">—</span>}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {rec.noun_modifier ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 text-xs font-semibold">
                                                                {rec.noun_modifier}
                                                            </span>
                                                        ) : <span className="text-slate-300">—</span>}
                                                    </td>
                                                    <td className="px-3 py-2 text-slate-600 max-w-[250px] truncate" title={rec.description}>
                                                        {rec.description || <span className="text-slate-300">—</span>}
                                                    </td>
                                                    <td className="px-3 py-2 text-center">
                                                        <span className="inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-bold">
                                                            {rec.property_count}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-center">
                                                        <span className="inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-bold">
                                                            {rec.value_count}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-center">
                                                        {rec.scraped_content_length > 0 ? (
                                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-600 text-xs font-medium">
                                                                <CheckCircle2 className="w-3 h-3" />
                                                                {(rec.scraped_content_length / 1024).toFixed(1)}k
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-300">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-xs text-slate-400">{rec.provider}</td>
                                                    <td className="px-3 py-2 text-center">
                                                        <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                                                            <button
                                                                onClick={() => handleEditRecord(rec)}
                                                                className="p-1 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                                                title="Edit record"
                                                            >
                                                                <Pencil className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleReprocessRecord(rec.id)}
                                                                disabled={reprocessingId === rec.id || !rec.source_url}
                                                                className="p-1 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                                title={rec.source_url ? 'Reprocess (re-scrape & re-map)' : 'No source URL'}
                                                            >
                                                                {reprocessingId === rec.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteRecord(rec.id)}
                                                                disabled={deletingRecordId === rec.id}
                                                                className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30"
                                                                title="Delete record"
                                                            >
                                                                {deletingRecordId === rec.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {/* Expanded row: show mapped output */}
                                                {expandedRecord === rec.id && rec.mapped_output && (
                                                    <tr className="bg-indigo-50/30">
                                                        <td colSpan={9} className="px-4 py-3">
                                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                                                {Object.entries(rec.mapped_output).map(([k, v]) => (
                                                                    <div key={k} className="bg-white rounded-lg px-3 py-2 border border-slate-100">
                                                                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{k}</p>
                                                                        <p className="text-sm text-slate-800 font-medium truncate" title={String(v || '')}>
                                                                            {v || <span className="text-slate-300 italic">empty</span>}
                                                                        </p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {recordsPagination.totalPages > 1 && (
                                <div className="flex items-center justify-between mt-3 px-1">
                                    <p className="text-xs text-slate-400">
                                        Page {recordsPagination.page} of {recordsPagination.totalPages} ({recordsPagination.total} records)
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => fetchVersionRecords(selectedVersionId, recordsPagination.page - 1)}
                                            disabled={recordsPagination.page <= 1}
                                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        >
                                            ← Prev
                                        </button>
                                        <button
                                            onClick={() => fetchVersionRecords(selectedVersionId, recordsPagination.page + 1)}
                                            disabled={recordsPagination.page >= recordsPagination.totalPages}
                                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Upload Section ── */}
            <div className="card">
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5 text-primary-600" />
                    Upload Training Data
                </h2>

                <div
                    className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-primary-400 hover:bg-primary-50/30 transition-all cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary-500', 'bg-primary-50'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50'); }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
                        const file = e.dataTransfer.files[0];
                        if (file) {
                            const fakeEvent = { target: { files: [file] } };
                            handleFileUpload(fakeEvent);
                        }
                    }}
                >
                    <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium">
                        {fileName ? `Loaded: ${fileName}` : 'Drop Excel file here or click to browse'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                        Expected columns: Part Number, Description, Noun/Modifier, URL, PROPERTY1..25, VALUE_OF_PROPERTY1..25
                    </p>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                />

                {/* Column mapping display */}
                {headers.length > 0 && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <p className="text-sm font-semibold text-slate-700 mb-2">Column Mapping:</p>
                        <div className="flex flex-wrap gap-3 text-sm">
                            {Object.entries(columnMapping).map(([key, col]) => (
                                <span key={key} className={`px-2.5 py-1 rounded-lg border ${col ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                                    <strong>{key}:</strong> {col || 'Not found'}
                                </span>
                            ))}
                            <span className="px-2.5 py-1 rounded-lg border bg-blue-50 text-blue-700 border-blue-200">
                                <strong>Properties:</strong> PROPERTY1..{MAX_PROPERTIES}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Data Preview & Processing ── */}
            {rows.length > 0 && (
                <div className="card">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5 text-primary-600" />
                            Data Preview ({rows.length} rows)
                        </h2>

                        <div className="flex items-center gap-3">
                            {/* Stats */}
                            <div className="hidden sm:flex items-center gap-2 text-sm">
                                <span className="text-slate-500">{withProps} with props</span>
                                <span className="text-slate-300">|</span>
                                <span className="text-slate-500">{withUrl} with URLs</span>
                                {completed > 0 && (
                                    <>
                                        <span className="text-slate-300">|</span>
                                        <span className="text-emerald-600 font-semibold">{completed} done</span>
                                    </>
                                )}
                                {failed > 0 && (
                                    <>
                                        <span className="text-slate-300">|</span>
                                        <span className="text-red-600 font-semibold">{failed} failed</span>
                                    </>
                                )}
                            </div>

                            {isProcessing ? (
                                <button onClick={stopProcessing} className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm transition-colors">
                                    <Square className="w-4 h-4" /> Stop
                                </button>
                            ) : (
                                <button onClick={processAllRows} className="btn-primary text-sm">
                                    <Play className="w-4 h-4" /> Process All ({rows.length} rows)
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Progress bar */}
                    {isProcessing && (
                        <div className="mb-4">
                            <div className="flex justify-between text-sm text-slate-600 mb-1">
                                <span>Processing row {processedCount + 1} of {rows.length}</span>
                                <span>{Math.round((processedCount / rows.length) * 100)}%</span>
                            </div>
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-primary-500 to-cyan-500 rounded-full transition-all duration-500"
                                    style={{ width: `${(processedCount / rows.length) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto rounded-xl border border-slate-200">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0 z-10">
                                <tr>
                                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600 w-12">#</th>
                                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Part Number</th>
                                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Description</th>
                                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Noun/Modifier</th>
                                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">URL</th>
                                    <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Props</th>
                                    <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Content</th>
                                    <th className="px-3 py-2.5 text-center font-semibold text-slate-600">Status</th>
                                    <th className="px-3 py-2.5 text-center font-semibold text-slate-600 w-10"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((row, i) => {
                                    const pn = columnMapping.partNumber ? String(row[columnMapping.partNumber] || '').trim() : '';
                                    const desc1 = columnMapping.description1 ? String(row[columnMapping.description1] || '').trim() : '';
                                    const desc2 = columnMapping.description2 ? String(row[columnMapping.description2] || '').trim() : '';
                                    const desc = [desc1, desc2].filter(Boolean).join('\n');
                                    const nm = columnMapping.nounModifier ? String(row[columnMapping.nounModifier] || '').trim() : '';
                                    const url = columnMapping.url ? String(row[columnMapping.url] || '').trim() : '';
                                    const isExpanded = expandedRow === i;

                                    return (
                                        <Fragment key={i}>
                                            <tr className={`hover:bg-slate-50 transition-colors ${row._status === 'completed' ? 'bg-emerald-50/30' : row._status === 'failed' ? 'bg-red-50/30' : ''}`}>
                                                <td className="px-3 py-2 text-slate-400 font-mono text-xs">{i + 1}</td>
                                                <td className="px-3 py-2 font-medium text-slate-900 max-w-[140px] truncate" title={pn}>{pn || '—'}</td>
                                                <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate" title={desc}>{desc || '—'}</td>
                                                <td className="px-3 py-2 text-slate-600 max-w-[120px] truncate" title={nm}>{nm || '—'}</td>
                                                <td className="px-3 py-2 max-w-[160px] truncate">
                                                    {url ? (
                                                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline text-xs">{url}</a>
                                                    ) : (
                                                        <span className="text-slate-300 text-xs">No URL</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    <span className="inline-flex items-center justify-center min-w-[28px] h-6 text-xs font-bold bg-slate-100 text-slate-600 rounded-md">
                                                        {row._propertyNames.length}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-center text-xs text-slate-500">
                                                    {row._cleanedContent || '—'}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    <StatusBadge status={row._status} />
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    <button onClick={() => setExpandedRow(isExpanded ? null : i)} className="text-slate-400 hover:text-slate-600">
                                                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                    </button>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan={9} className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                                            <div>
                                                                <p className="font-semibold text-slate-700 mb-1">Properties & Values:</p>
                                                                <div className="space-y-0.5">
                                                                    {row._propertyNames.map((p, pi) => (
                                                                        <div key={pi} className="flex gap-2">
                                                                            <span className="text-slate-500 min-w-[140px]">{p}:</span>
                                                                            <span className="text-slate-800 font-medium">{row._mappedOutput[p] || '—'}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            {row._error && (
                                                                <div>
                                                                    <p className="font-semibold text-red-600 mb-1">Error:</p>
                                                                    <p className="text-red-500">{row._error}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Training Section ── */}
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <Brain className="w-5 h-5 text-violet-600" />
                        Model Training
                    </h2>
                </div>

                {/* ── Training Configuration ── */}
                {!isTraining && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-slate-50 to-violet-50/30 border border-slate-200 rounded-xl">
                        <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            ⚙️ Training Configuration
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {/* Epochs */}
                            <div>
                                <label htmlFor="training-epochs" className="block text-xs font-semibold text-slate-600 mb-1.5">
                                    Epochs
                                </label>
                                <div className="relative">
                                    <input
                                        id="training-epochs"
                                        type="number"
                                        min="1"
                                        max="100"
                                        value={customEpochs}
                                        onChange={(e) => setCustomEpochs(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                                        className="input-field text-center text-lg font-bold pr-12"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-medium">rounds</span>
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1">More epochs = better accuracy but longer training</p>
                            </div>

                            {/* Batch Size */}
                            <div>
                                <label htmlFor="training-batch-size" className="block text-xs font-semibold text-slate-600 mb-1.5">
                                    Batch Size
                                </label>
                                <div className="relative">
                                    <select
                                        id="training-batch-size"
                                        value={customBatchSize}
                                        onChange={(e) => setCustomBatchSize(parseInt(e.target.value))}
                                        className="input-field text-center text-lg font-bold appearance-none"
                                    >
                                        <option value={1}>1</option>
                                        <option value={2}>2</option>
                                        <option value={4}>4</option>
                                        <option value={8}>8</option>
                                        <option value={16}>16</option>
                                    </select>
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1">Larger = faster training but needs more GPU memory</p>
                            </div>

                            {/* Learning Rate */}
                            <div>
                                <label htmlFor="training-lr" className="block text-xs font-semibold text-slate-600 mb-1.5">
                                    Learning Rate
                                </label>
                                <div className="relative">
                                    <select
                                        id="training-lr"
                                        value={customLearningRate}
                                        onChange={(e) => setCustomLearningRate(e.target.value)}
                                        className="input-field text-center text-lg font-bold appearance-none"
                                    >
                                        <option value="1e-4">1e-4 (Conservative)</option>
                                        <option value="2e-4">2e-4 (Moderate)</option>
                                        <option value="3e-4">3e-4 (Default)</option>
                                        <option value="5e-4">5e-4 (Aggressive)</option>
                                        <option value="1e-3">1e-3 (Fast)</option>
                                    </select>
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1">How fast the model learns (3e-4 recommended)</p>
                            </div>
                        </div>

                        {/* Quick Presets */}
                        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                            <span className="text-xs font-semibold text-slate-500">Quick Presets:</span>
                            <button
                                onClick={() => { setCustomEpochs(5); setCustomBatchSize(4); setCustomLearningRate('3e-4'); }}
                                className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                            >
                                ⚡ Quick (5 epochs)
                            </button>
                            <button
                                onClick={() => { setCustomEpochs(10); setCustomBatchSize(4); setCustomLearningRate('3e-4'); }}
                                className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition-colors"
                            >
                                ⭐ Standard (10 epochs)
                            </button>
                            <button
                                onClick={() => { setCustomEpochs(20); setCustomBatchSize(2); setCustomLearningRate('2e-4'); }}
                                className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                            >
                                🎯 Deep (20 epochs)
                            </button>
                            <button
                                onClick={() => { setCustomEpochs(50); setCustomBatchSize(2); setCustomLearningRate('1e-4'); }}
                                className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
                            >
                                🔥 Maximum (50 epochs)
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Start Training Button ── */}
                <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-slate-500">
                        {!isTraining && (
                            <span>
                                Will train for <strong className="text-violet-700">{customEpochs} epochs</strong> with batch size <strong className="text-violet-700">{customBatchSize}</strong> at LR <strong className="text-violet-700">{customLearningRate}</strong>
                            </span>
                        )}
                    </div>
                    <button
                        onClick={handleStartTraining}
                        disabled={isTraining || (trainingStatus?.stats?.total || 0) < 5}
                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                            isTraining || (trainingStatus?.stats?.total || 0) < 5
                                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-sm hover:shadow-md'
                        }`}
                    >
                        {isTraining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                        {isTraining ? 'Training...' : `Start Training (${customEpochs} epochs)`}
                    </button>
                </div>

                {/* ── Live Training Progress ── */}
                {(isTraining || (trainingProgress && trainingProgress.status === 'training')) && trainingProgress && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl">
                        {/* Progress Bar */}
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold text-violet-800">
                                Training in Progress
                            </span>
                            <span className="text-sm font-bold text-violet-700">
                                {trainingProgress.percent || 0}%
                            </span>
                        </div>
                        <div className="w-full bg-violet-100 rounded-full h-3 mb-3 overflow-hidden">
                            <div
                                className="bg-gradient-to-r from-violet-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                                style={{ width: `${trainingProgress.percent || 0}%` }}
                            />
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div className="bg-white/70 rounded-lg p-2 text-center">
                                <div className="text-violet-500 font-medium">Epoch</div>
                                <div className="text-lg font-bold text-slate-800">
                                    {trainingProgress.epoch || 0} / {trainingProgress.total_epochs || '?'}
                                </div>
                            </div>
                            <div className="bg-white/70 rounded-lg p-2 text-center">
                                <div className="text-violet-500 font-medium">Step</div>
                                <div className="text-lg font-bold text-slate-800">
                                    {trainingProgress.global_step || 0} / {trainingProgress.total_steps || '?'}
                                </div>
                            </div>
                            <div className="bg-white/70 rounded-lg p-2 text-center">
                                <div className="text-violet-500 font-medium">Loss</div>
                                <div className="text-lg font-bold text-slate-800">
                                    {trainingProgress.loss != null ? trainingProgress.loss.toFixed(4) : '—'}
                                </div>
                            </div>
                            <div className="bg-white/70 rounded-lg p-2 text-center">
                                <div className="text-violet-500 font-medium">ETA</div>
                                <div className="text-lg font-bold text-slate-800">
                                    {trainingProgress.eta_seconds != null
                                        ? trainingProgress.eta_seconds >= 3600
                                            ? `${Math.floor(trainingProgress.eta_seconds / 3600)}h ${Math.floor((trainingProgress.eta_seconds % 3600) / 60)}m`
                                            : trainingProgress.eta_seconds >= 60
                                                ? `${Math.floor(trainingProgress.eta_seconds / 60)}m ${trainingProgress.eta_seconds % 60}s`
                                                : `${trainingProgress.eta_seconds}s`
                                        : '—'}
                                </div>
                            </div>
                        </div>

                        {/* Records info */}
                        <div className="mt-2 text-xs text-violet-600 text-center">
                            Training on {trainingProgress.total_records || '?'} records •
                            Step {trainingProgress.step || 0}/{trainingProgress.steps_per_epoch || '?'} in epoch
                            {trainingProgress.best_val_loss != null && ` • Best val_loss: ${trainingProgress.best_val_loss.toFixed(4)}`}
                        </div>
                    </div>
                )}

                {/* ── Training Completed Banner ── */}
                {trainingProgress && trainingProgress.status === 'completed' && !isTraining && (
                    <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-1">
                            <CheckCircle2 className="w-4 h-4" />
                            Training Completed!
                        </div>
                        <div className="text-xs text-emerald-600">
                            Final loss: {trainingProgress.loss?.toFixed(4) || 'N/A'}
                            {trainingProgress.best_val_loss != null && ` • Best val_loss: ${trainingProgress.best_val_loss.toFixed(4)}`}
                            {trainingProgress.elapsed_seconds && ` • Duration: ${Math.floor(trainingProgress.elapsed_seconds / 60)}m ${trainingProgress.elapsed_seconds % 60}s`}
                        </div>
                    </div>
                )}

                {(() => {
                    const effectiveTotal = selectedVersionId && versionStats ? versionStats.total : (trainingStatus?.stats?.total || 0);
                    const effectiveLog = selectedVersionId && versionStats?.trainingLog
                        ? versionStats.trainingLog
                        : trainingStatus?.trainingLog;

                    return (
                        <>
                            {effectiveTotal < 5 && (
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                                    Need at least <strong>5 training records</strong> to start training. Currently have {effectiveTotal}.
                                    Process rows above to add training data.
                                </div>
                            )}

                            {effectiveLog && effectiveLog.length > 0 && (
                                <div className="mt-4">
                                    <p className="text-sm font-semibold text-slate-700 mb-2">Last Training Results:</p>
                                    <div className="overflow-x-auto rounded-xl border border-slate-200">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left font-semibold text-slate-600">Epoch</th>
                                                    <th className="px-4 py-2 text-left font-semibold text-slate-600">Train Loss</th>
                                                    <th className="px-4 py-2 text-left font-semibold text-slate-600">Val Loss</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {effectiveLog.map((entry, i) => (
                                                    <tr key={i} className="hover:bg-slate-50">
                                                        <td className="px-4 py-2 font-medium">{entry.epoch}</td>
                                                        <td className="px-4 py-2 font-mono text-slate-700">{entry.train_loss?.toFixed(4)}</td>
                                                        <td className="px-4 py-2 font-mono text-slate-700">{entry.val_loss?.toFixed(4) || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>

            {/* ── Processing Log ── */}
            {logs.length > 0 && (
                <div className="card">
                    <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setShowLogs(!showLogs)}
                    >
                        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                            <Eye className="w-5 h-5 text-slate-500" />
                            Processing Log ({logs.length})
                        </h2>
                        {showLogs ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>

                    {showLogs && (
                        <div className="mt-3 bg-slate-900 rounded-xl p-4 max-h-[300px] overflow-y-auto font-mono text-xs">
                            {logs.map((log, i) => (
                                <div key={i} className={`py-0.5 ${
                                    log.type === 'error' ? 'text-red-400' :
                                    log.type === 'success' ? 'text-emerald-400' :
                                    log.type === 'warn' ? 'text-amber-400' :
                                    'text-slate-300'
                                }`}>
                                    <span className="text-slate-500">{log.ts}</span>{' '}
                                    {log.msg}
                                </div>
                            ))}
                            <div ref={logEndRef} />
                        </div>
                    )}
                </div>
            )}

            {/* ── Reset Confirmation Modal ── */}
            {resetModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl border border-slate-200">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-10 w-10 rounded-xl bg-red-100 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-900">
                                    {resetModal === 'records' && 'Reset Training Records?'}
                                    {resetModal === 'content' && 'Reset Cleaned Content?'}
                                    {resetModal === 'model' && 'Reset AI Trained Memory?'}
                                </h3>
                            </div>
                        </div>

                        <p className="text-sm text-slate-600 mb-4">
                            {resetModal === 'records' && 'This will permanently delete ALL training records from the database. This action cannot be undone.'}
                            {resetModal === 'content' && 'This will clear all scraped/cleaned content from training records, but keep the property mappings.'}
                            {resetModal === 'model' && 'This will permanently delete the trained model files. The AI will lose all its learned mappings and you will need to retrain from scratch.'}
                        </p>

                        {resetModal === 'model' && (
                            <div className="mb-4">
                                <p className="text-sm font-semibold text-red-600 mb-2">
                                    Type <code className="bg-red-50 px-2 py-0.5 rounded text-red-700 font-bold">RESET</code> to confirm:
                                </p>
                                <input
                                    type="text"
                                    value={resetConfirmText}
                                    onChange={(e) => setResetConfirmText(e.target.value)}
                                    placeholder="Type RESET"
                                    className="input-field border-red-300 focus:ring-red-100 focus:border-red-400"
                                    autoFocus
                                />
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => { setResetModal(null); setResetConfirmText(''); }}
                                className="flex-1 btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleReset(resetModal)}
                                disabled={resetModal === 'model' && resetConfirmText !== 'RESET'}
                                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
                                    resetModal === 'model' && resetConfirmText !== 'RESET'
                                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                        : 'bg-red-600 hover:bg-red-700 text-white'
                                }`}
                            >
                                <Trash2 className="w-4 h-4" />
                                {resetModal === 'model' ? 'Reset AI Memory' : 'Reset'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* ── Edit Training Record Modal ── */}
            {editingRecord && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setEditingRecord(null)}>
                    <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-xl border border-slate-200 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                <Pencil className="w-5 h-5 text-blue-600" />
                                Edit Record #{editingRecord.id}
                            </h3>
                            <button onClick={() => setEditingRecord(null)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Part Number (read-only) */}
                        <div className="mb-3">
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Part Number</label>
                            <div className="input-field bg-slate-50 text-slate-600 cursor-not-allowed">
                                {editingRecord.manufacturer_pn || '—'}
                            </div>
                        </div>

                        {/* Description */}
                        <div className="mb-3">
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
                            <textarea
                                value={editForm.description}
                                onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                                rows={2}
                                className="input-field"
                            />
                        </div>

                        {/* Noun/Modifier */}
                        <div className="mb-4">
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Noun/Modifier</label>
                            <input
                                type="text"
                                value={editForm.nounModifier}
                                onChange={e => setEditForm(prev => ({ ...prev, nounModifier: e.target.value }))}
                                className="input-field"
                            />
                        </div>

                        {/* Mapped Output */}
                        <div className="mb-4">
                            <label className="block text-xs font-semibold text-slate-500 mb-2">Property Values</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {Object.entries(editForm.mappedOutput || {}).map(([key, val]) => (
                                    <div key={key} className="flex flex-col gap-1">
                                        <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{key}</label>
                                        <input
                                            type="text"
                                            value={val || ''}
                                            onChange={e => setEditForm(prev => ({
                                                ...prev,
                                                mappedOutput: { ...prev.mappedOutput, [key]: e.target.value }
                                            }))}
                                            className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-colors"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-2 border-t border-slate-100">
                            <button
                                onClick={() => setEditingRecord(null)}
                                className="flex-1 btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                disabled={savingEdit}
                                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
                            >
                                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
